/**
 * Guided tour: серверный state + DOM-гейт + driver.js spotlight.
 * Не монтируется для гостей и путей /admin*.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { driver, type Driver } from "driver.js";
import "driver.js/dist/driver.css";

import { useAuth } from "@/hooks/useAuthV2";
import { useModuleFlags } from "@/contexts/ModuleFlagsContext";
import {
  useCompleteOnboardingStep,
  useDismissOnboardingTour,
  useOnboardingState,
} from "@/hooks/useOnboarding";
import { markTourSeenUi, setPendingTour } from "@/lib/onboarding-adapter";
import type { OnboardingStepDto } from "@/services/onboarding-api.service";

const POLL_MS = 300;
const MAX_WAIT_MS = 60_000;

function waitForSelector(selector: string, signal: AbortSignal): Promise<Element | null> {
  return new Promise((resolve) => {
    const started = Date.now();
    const tick = () => {
      if (signal.aborted) {
        resolve(null);
        return;
      }
      try {
        const el = document.querySelector(selector);
        if (el) {
          resolve(el);
          return;
        }
      } catch {
        resolve(null);
        return;
      }
      if (Date.now() - started > MAX_WAIT_MS) {
        resolve(null);
        return;
      }
      window.setTimeout(tick, POLL_MS);
    };
    tick();
  });
}

function mapPlacement(placement?: string | null): "top" | "bottom" | "left" | "right" {
  if (placement === "top" || placement === "left" || placement === "right") return placement;
  return "bottom";
}

export function OnboardingProvider() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const { flags } = useModuleFlags();
  const isAdminPath = location.pathname.startsWith("/admin");
  const moduleOn = flags.moduleOnboardingEnabled !== false;
  const enabled = Boolean(isAuthenticated && user?.id && moduleOn && !isAdminPath);

  const { data: state } = useOnboardingState(enabled);
  const completeStep = useCompleteOnboardingStep();
  const dismissTour = useDismissOnboardingTour();

  const driverRef = useRef<Driver | null>(null);
  const runningTourKey = useRef<string | null>(null);
  const finishedByServer = useRef(false);

  useEffect(() => {
    if (!enabled || !user?.id) return;

    if (state?.enabled && state.tour_key) {
      setPendingTour(user.id, state.tour_key);
    }

    if (!state?.enabled || state.is_completed || !state.tour_key) {
      finishedByServer.current = true;
      driverRef.current?.destroy();
      driverRef.current = null;
      runningTourKey.current = null;
      return;
    }

    const tourKey = state.tour_key;
    const steps = (state.steps || []).slice(state.current_step_order);
    if (steps.length === 0) return;

    if (runningTourKey.current === tourKey && driverRef.current) {
      return;
    }

    const ac = new AbortController();
    let cancelled = false;

    const launch = async () => {
      const first = steps[0];
      const gateSelector = first.wait_for_selector || first.element_selector;
      if (gateSelector) {
        const found = await waitForSelector(gateSelector, ac.signal);
        if (!found || cancelled) return;
      }

      finishedByServer.current = false;
      driverRef.current?.destroy();
      runningTourKey.current = tourKey;
      setPendingTour(user.id, tourKey);

      const d = driver({
        showProgress: true,
        allowClose: true,
        overlayOpacity: 0.55,
        stagePadding: 6,
        nextBtnText: "Далее",
        prevBtnText: "Назад",
        doneBtnText: "Готово",
        progressText: "{{current}} / {{total}}",
        onDestroyStarted: () => {
          const key = runningTourKey.current;
          const wasFinished = finishedByServer.current;
          if (!wasFinished) {
            // Закрытие крестиком / overlay → dismiss на сервере
            if (key) {
              markTourSeenUi(user.id, key);
              setPendingTour(user.id, null);
              void dismissTour.mutateAsync(key).catch(() => undefined);
            }
          }
          d.destroy();
          driverRef.current = null;
          runningTourKey.current = null;
        },
        steps: steps.map((step: OnboardingStepDto) => ({
          element: step.element_selector || undefined,
          popover: {
            title: step.title || state.tour_name || "Подсказка",
            description: step.content || "",
            side: mapPlacement(step.placement),
            align: "start",
            onNextClick: (_el, _step, opts) => {
              void (async () => {
                try {
                  const res = await completeStep.mutateAsync(tourKey);
                  if (res.is_finished) {
                    finishedByServer.current = true;
                    markTourSeenUi(user.id, tourKey);
                    setPendingTour(user.id, null);
                    runningTourKey.current = null;
                    opts.driver.destroy();
                    driverRef.current = null;
                    return;
                  }
                  opts.driver.moveNext();
                } catch {
                  /* leave tour open */
                }
              })();
            },
          },
        })),
      });

      driverRef.current = d;
      d.drive();
    };

    void launch();

    return () => {
      cancelled = true;
      ac.abort();
    };
    // complete/dismiss мутации стабильны по сути; driver привязан к tour key/order
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    user?.id,
    state?.enabled,
    state?.tour_key,
    state?.current_step_order,
    state?.is_completed,
    location.pathname,
  ]);

  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
      driverRef.current = null;
      runningTourKey.current = null;
    };
  }, []);

  return null;
}
